import { useTranslation } from 'next-i18next';
import { usePdp } from '@context/PdpContext';
import { useUI } from '@context/GlobalUIContext';
import IconWrapper from '@components/ui/Icons';
import type { PriceGroup } from '@adapters/bigcommerce/types/product';
import usePrice from '@hooks/usePrice';
import PriceContainer from '_components/Product/PriceContainer';

import styles from './PdpPriceContainerView.module.scss';

export interface PdpPriceContainerViewProps {
  price: PriceGroup;
  priceString: string | null;
  originalPriceString: string | null;
  showGroupLabel: boolean;
  customerGroupMessage: string;
  isInCartDiscountTag: boolean;
  tagInfoModalPath: string | undefined;
}

/**
 * PDP flag-on branch: ED-33 layout, `_components` price block, and PDP-only i18n / discount math.
 */
const PdpPriceContainerView = ({
  price,
  priceString,
  originalPriceString,
  showGroupLabel,
  customerGroupMessage,
  isInCartDiscountTag,
  tagInfoModalPath,
}: PdpPriceContainerViewProps) => {
  const { t: tPdp } = useTranslation('pdp');
  const {
    promoBadge,
    hasMemberPriceDifference,
    customerGroupPrices,
    selectedVariant,
    actualPriceList,
  } = usePdp();
  const { openTagInfoModal } = useUI();
  const formatPrice = usePrice();

  const currentPriceValue = price.price?.value ?? price.lowPrice?.value;
  const originalPriceValue =
    price.basePrice?.value ?? price.baseLowPrice?.value;

  const computedDiscountPercentage =
    currentPriceValue &&
    originalPriceValue &&
    originalPriceValue > currentPriceValue
      ? Math.round(
          ((originalPriceValue - currentPriceValue) / originalPriceValue) * 100
        )
      : null;

  const guestPrice = selectedVariant
    ? customerGroupPrices?.guest?.variants.find(
        (variant) => variant.entityId === selectedVariant.entityId
      )?.price
    : customerGroupPrices?.guest?.price;
  const guestOriginalPriceValue =
    guestPrice?.price?.value ?? guestPrice?.lowPrice?.value;

  const discountPercentage =
    promoBadge?.discount_percentage ?? computedDiscountPercentage;
  const isMemberDiscount =
    hasMemberPriceDifference &&
    actualPriceList.groupKey !== 'guest' &&
    promoBadge?.code !== 'CLEARANCE' &&
    promoBadge?.code !== 'SALE';
  const memberOriginalPriceString =
    isMemberDiscount &&
    guestOriginalPriceValue &&
    currentPriceValue &&
    guestOriginalPriceValue > currentPriceValue
      ? formatPrice(guestOriginalPriceValue)
      : isMemberDiscount && originalPriceValue
      ? formatPrice(originalPriceValue)
      : null;
  const effectiveOriginalPriceString =
    originalPriceString ?? memberOriginalPriceString;
  const salePriceAriaLabel =
    effectiveOriginalPriceString && priceString
      ? tPdp('product.price.aria.discounted', {
          currentPrice: priceString,
          originalPrice: effectiveOriginalPriceString,
        })
      : undefined;
  const discountLabel = discountPercentage
    ? tPdp(
        promoBadge?.multi_discount
          ? 'product.price.discount.percentage.upTo'
          : 'product.price.discount.percentage',
        { percent: discountPercentage }
      )
    : null;

  const isMemberBadge =
    promoBadge && 'isMemberBadge' in promoBadge && promoBadge.isMemberBadge;

  const memberBadgeKeys: Record<string, string> = {
    SALE: 'product.badge.member.sale',
    CLEARANCE: 'product.badge.member.clearance',
  };

  const getBadgeText = () => {
    if (isMemberBadge) {
      const memberBadgeKey = memberBadgeKeys[promoBadge?.code ?? ''];
      return memberBadgeKey ? tPdp(memberBadgeKey) : discountLabel;
    }
    return discountLabel;
  };

  return (
    <div>
      {showGroupLabel && (
        <div className={styles.memberGroupLabel}>{customerGroupMessage}</div>
      )}
      <div className={styles.priceRow}>
        <PriceContainer
          price={priceString}
          isMemberDiscount={isMemberDiscount}
          originalPrice={effectiveOriginalPriceString}
          salePriceAriaLabel={salePriceAriaLabel}
          hasInCartDiscountTag={isInCartDiscountTag}
          hasMemberGroupPrice={showGroupLabel}
          discountLabel={getBadgeText()}
        />
        {isInCartDiscountTag && (
          <button
            type="button"
            className={styles.infoIcon}
            onClick={() => openTagInfoModal({ path: tagInfoModalPath })}
          >
            <IconWrapper icon="info" width={20} />
          </button>
        )}
      </div>
    </div>
  );
};

export default PdpPriceContainerView;
